// The renderer half of a two-sided sanitisation. `xoe-agent`'s generation pipeline
// sanitises the same fields server-side; this runs again because a row written before that
// pipeline existed is still read by this component, and `description` is model output.
//
// ⚠️ NO ATTRIBUTE IS EVER EMITTED. An allow-list of tags with zero attributes cannot carry
// an `on*` handler, a `javascript:` URL, an inline `style` or a `class` — so the theme
// requirement (`--mg-*` tokens only, never a model-chosen colour) holds by construction.

const ALLOWED_TAGS = new Set([
	'p', 'h3', 'h4', 'ul', 'ol', 'li', 'strong', 'em',
	'table', 'thead', 'tbody', 'tr', 'th', 'td', 'br',
]);

// Their text content is discarded rather than unwrapped.
const DROP_CONTENT_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'LINK', 'NOSCRIPT']);

const VOID_TAGS = new Set(['br']);

const escapeText = (value: string): string =>
	value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const serialise = (node: Node): string => {
	if (node.nodeType === Node.TEXT_NODE) return escapeText(node.nodeValue ?? '');
	if (node.nodeType !== Node.ELEMENT_NODE) return '';

	const element = node as Element;
	if (DROP_CONTENT_TAGS.has(element.tagName)) return '';

	const tag = element.tagName.toLowerCase();
	const children = Array.from(element.childNodes).map(serialise).join('');

	if (!ALLOWED_TAGS.has(tag)) return children; // Unwrap: the prose inside is still the report.
	if (VOID_TAGS.has(tag)) return `<${tag}>`;
	return `<${tag}>${children}</${tag}>`;
};

export const sanitizeHtml = (value: unknown): string => {
	if (typeof value !== 'string' || value.trim().length === 0) return '';
	// `DOMParser` does not execute scripts or load resources, so parsing untrusted markup is safe.
	const parsed = new DOMParser().parseFromString(value, 'text/html');
	return Array.from(parsed.body.childNodes).map(serialise).join('').trim();
};

// Legacy rows store prose as plain text with blank-line paragraph breaks; the current
// pipeline stores HTML. Both reach this component, so both have to render.
const looksLikeHtml = (value: string): boolean => /<\/?[a-z][a-z0-9]*\s*\/?>/i.test(value);

export const toSafeHtml = (value: unknown): string => {
	if (typeof value !== 'string' || value.trim().length === 0) return '';
	if (looksLikeHtml(value)) return sanitizeHtml(value);
	return value
		.split(/\n{2,}/)
		.map((paragraph) => `<p>${escapeText(paragraph.trim())}</p>`)
		.filter((paragraph) => paragraph !== '<p></p>')
		.join('');
};

// ⚠️ THE 3060 LEGACY ROWS. 97% of `panel_ai_data` predates the JSON payload and stores the
// whole report in `description` as one blob using `<b>Heading</b>` markers, with the
// per-section columns empty. They were already rendered through an UNSANITISED
// `dangerouslySetInnerHTML`, so this path is a security fix as much as a formatting one.
//
// The heading rewrite runs on the RAW string and the result is sanitised afterwards, so
// whatever the rewrite produces still cannot carry an attribute.
const HEADING_OPEN = '\u0001';
const HEADING_CLOSE = '\u0002';

export const legacyReportToHtml = (raw: unknown): string => {
	if (typeof raw !== 'string' || raw.trim().length === 0) return '';

	const marked = raw
		.replace(/<b>\s*(.*?)\s*<\/b>/gi, `\n\n${HEADING_OPEN}$1${HEADING_CLOSE}\n\n`)
		.replace(/<i>(.*?)<\/i>/gi, '<em>$1</em>');

	const html = marked
		.split(/\n{2,}/)
		.map((block) => block.trim())
		.filter(Boolean)
		.map((block) => {
			if (block.startsWith(HEADING_OPEN) && block.endsWith(HEADING_CLOSE)) {
				return `<h3>${block.slice(1, -1)}</h3>`;
			}
			return `<p>${block.replace(/\n/g, '<br>')}</p>`;
		})
		.join('');

	return sanitizeHtml(html);
};
